// src/pages/Archive/index.jsx
import { useState, useEffect } from 'react';
import { Card, Typography, List, Divider, Space, Tag, Spin, Row, Col, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { 
  CalendarOutlined, 
  EyeOutlined, 
  LikeOutlined, 
  UserOutlined, 
  FileTextOutlined, 
  FireOutlined,
  RiseOutlined,
  TrophyOutlined,
  StarOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { fakeApi } from '../../api/fakeApi';

// 导入 Recharts
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const { Title, Text } = Typography;

const Archive = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({
    monthlyData: [],
    tagData: [],
    authorData: [],
    activityData: [],
    heatData: []
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await fakeApi.posts.getPosts(1, 100);
      if (response.success) {
        const sorted = [...response.data.posts].sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        setPosts(sorted);
        processChartData(sorted);
      }
    } catch (error) {
      console.error('获取文章失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (posts) => {
    const monthlyMap = {};
    const tagMap = {};
    const authorMap = {};

    // 处理数据
    posts.forEach(post => {
      // 月度数据
      if (post.date) {
        const month = post.date.substring(0, 7);
        monthlyMap[month] = (monthlyMap[month] || 0) + 1;
      }

      // 标签数据
      if (post.tags && Array.isArray(post.tags)) {
        post.tags.forEach(tag => {
          tagMap[tag] = (tagMap[tag] || 0) + 1;
        });
      }

      // 作者数据
      if (post.author) {
        authorMap[post.author] = (authorMap[post.author] || 0) + 1;
      }
    });

    // 月度数据转换
    const monthlyData = Object.keys(monthlyMap)
      .map(month => ({
        name: `${month}月`,
        文章数量: monthlyMap[month]
      }))
      .slice(-6) // 最近6个月
      .reverse();

    // 标签数据转换
    const tagData = Object.keys(tagMap)
      .map(tag => ({
        name: tag,
        value: tagMap[tag]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    // 作者数据转换
    const authorData = Object.keys(authorMap)
      .map(author => ({
        name: author,
        文章数量: authorMap[author]
      }))
      .sort((a, b) => b.文章数量 - a.文章数量)
      .slice(0, 5);

    // 用户活跃度数据
    const activityData = [
      { date: '1月1日', 阅读量: 120, 点赞数: 45, 评论数: 12 },
      { date: '1月2日', 阅读量: 150, 点赞数: 52, 评论数: 18 },
      { date: '1月3日', 阅读量: 180, 点赞数: 67, 评论数: 22 },
      { date: '1月4日', 阅读量: 210, 点赞数: 78, 评论数: 25 },
      { date: '1月5日', 阅读量: 190, 点赞数: 62, 评论数: 20 },
      { date: '1月6日', 阅读量: 230, 点赞数: 84, 评论数: 28 },
      { date: '1月7日', 阅读量: 260, 点赞数: 95, 评论数: 32 },
    ];

    // 热度数据（文章热度排行）
    const heatData = posts
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 5)
      .map((post, index) => ({
        name: post.title.length > 10 ? post.title.substring(0, 10) + '...' : post.title,
        阅读量: post.views || 0,
        点赞数: post.likes || 0,
        热度指数: Math.round(((post.views || 0) * 0.5 + (post.likes || 0) * 0.3 + (post.comments || 0) * 0.2) / 10),
        排名: index + 1
      }));

    setChartData({
      monthlyData,
      tagData,
      authorData,
      activityData,
      heatData
    });
  };

  // 饼图颜色
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

  // 折线图颜色
  const lineColors = {
    阅读量: '#1890ff',
    点赞数: '#52c41a',
    评论数: '#f5222d'
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载归档数据...</div>
      </div>
    );
  }

  // 计算统计数据
  const totalViews = posts.reduce((sum, post) => sum + (post.views || 0), 0);
  const totalLikes = posts.reduce((sum, post) => sum + (post.likes || 0), 0);
  const totalComments = posts.reduce((sum, post) => sum + (post.comments || 0), 0);
  const uniqueAuthors = new Set(posts.map(p => p.author).filter(Boolean)).size;

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Title level={2}>文章归档</Title>
        <Text type="secondary">共 {posts.length} 篇文章</Text>
        
        <Divider orientation="left" style={{ marginTop: 40 }}>
          📊 数据统计概览
        </Divider>
        
        {/* 统计卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" hoverable>
              <Space direction="vertical" align="center" style={{ width: '100%' }}>
                <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                <Text strong>文章总数</Text>
                <Title level={3} style={{ margin: 0 }}>{posts.length}</Title>
              </Space>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card size="small" hoverable>
              <Space direction="vertical" align="center" style={{ width: '100%' }}>
                <EyeOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                <Text strong>总阅读量</Text>
                <Title level={3} style={{ margin: 0 }}>{totalViews.toLocaleString()}</Title>
              </Space>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card size="small" hoverable>
              <Space direction="vertical" align="center" style={{ width: '100%' }}>
                <LikeOutlined style={{ fontSize: 24, color: '#f5222d' }} />
                <Text strong>总点赞数</Text>
                <Title level={3} style={{ margin: 0 }}>{totalLikes.toLocaleString()}</Title>
              </Space>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card size="small" hoverable>
              <Space direction="vertical" align="center" style={{ width: '100%' }}>
                <UserOutlined style={{ fontSize: 24, color: '#fa8c16' }} />
                <Text strong>作者数量</Text>
                <Title level={3} style={{ margin: 0 }}>{uniqueAuthors}</Title>
              </Space>
            </Card>
          </Col>
        </Row>
        
        {/* 图表区域 */}
        <Row gutter={[16, 16]}>
          {/* 月度文章趋势 - 柱状图 */}
          <Col xs={24} md={12}>
            <Card 
              title={
                <Space>
                  <RiseOutlined style={{ color: '#1890ff' }} />
                  <Text strong>月度文章发布趋势</Text>
                </Space>
              } 
              size="small"
            >
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar 
                      dataKey="文章数量" 
                      fill="#1890ff" 
                      radius={[4, 4, 0, 0]}
                      name="文章数量"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          
          {/* 标签分布 - 饼图 */}
          <Col xs={24} md={12}>
            <Card 
              title={
                <Space>
                  <FireOutlined style={{ color: '#f5222d' }} />
                  <Text strong>热门标签分布</Text>
                </Space>
              } 
              size="small"
            >
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={chartData.tagData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.tagData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => [`${value}篇文章`, '数量']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          
          {/* 作者贡献 - 水平柱状图 */}
          <Col xs={24} md={12}>
            <Card 
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#fa8c16' }} />
                  <Text strong>作者文章贡献榜</Text>
                </Space>
              } 
              size="small"
            >
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <BarChart 
                    data={chartData.authorData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 50, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={80}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip formatter={(value) => [`${value}篇文章`, '文章数量']} />
                    <Legend />
                    <Bar 
                      dataKey="文章数量" 
                      fill="#fa8c16" 
                      radius={[0, 4, 4, 0]}
                      name="文章数量"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          
          {/* 用户活跃度 - 折线图 */}
          <Col xs={24} md={12}>
            <Card 
              title={
                <Space>
                  <StarOutlined style={{ color: '#eb2f96' }} />
                  <Text strong>用户活跃度趋势</Text>
                </Space>
              } 
              size="small"
            >
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData.activityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="阅读量" 
                      stroke={lineColors.阅读量} 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="阅读量"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="点赞数" 
                      stroke={lineColors.点赞数} 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="点赞数"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="评论数" 
                      stroke={lineColors.评论数} 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="评论数"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 文章热度排行 - 面积图 */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card 
              title={
                <Space>
                  <FireOutlined style={{ color: '#f5222d' }} />
                  <Text strong>热门文章热度排行</Text>
                </Space>
              } 
              size="small"
            >
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <AreaChart
                    data={chartData.heatData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="热度指数"
                      stackId="1"
                      stroke="#ff7300"
                      fill="#ff7300"
                      fillOpacity={0.6}
                      name="热度指数"
                    />
                    <Area
                      type="monotone"
                      dataKey="阅读量"
                      stackId="2"
                      stroke="#387908"
                      fill="#82ca9d"
                      fillOpacity={0.6}
                      name="阅读量"
                    />
                    <Area
                      type="monotone"
                      dataKey="点赞数"
                      stackId="3"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.6}
                      name="点赞数"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 互动统计汇总 */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={8}>
            <Card size="small" title="📈 互动率统计">
              <div style={{ height: 200 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        { name: '阅读率', value: 65 },
                        { name: '点赞率', value: 25 },
                        { name: '评论率', value: 10 }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      <Cell fill="#1890ff" />
                      <Cell fill="#52c41a" />
                      <Cell fill="#f5222d" />
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} md={16}>
            <Card size="small" title="📊 详细统计数据">
              <List
                size="small"
                dataSource={[
                  { label: '总评论数', value: totalComments, icon: <MessageOutlined />, color: '#1890ff' },
                  { label: '平均阅读量', value: posts.length > 0 ? Math.round(totalViews / posts.length) : 0, icon: <EyeOutlined />, color: '#52c41a' },
                  { label: '平均点赞率', value: totalViews > 0 ? `${((totalLikes / totalViews) * 100).toFixed(1)}%` : '0%', icon: <LikeOutlined />, color: '#f5222d' },
                  { label: '作者人均文章', value: uniqueAuthors > 0 ? (posts.length / uniqueAuthors).toFixed(1) : 0, icon: <UserOutlined />, color: '#fa8c16' },
                ]}
                renderItem={item => (
                  <List.Item>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <div style={{ color: item.color }}>{item.icon}</div>
                        <Text>{item.label}</Text>
                      </Space>
                      <Text strong style={{ fontSize: 18 }}>{item.value}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>

        <Divider orientation="left" style={{ marginTop: 40 }}>
          📄 文章列表
        </Divider>
        
        <List
          dataSource={posts}
          renderItem={post => (
            <List.Item>
              <List.Item.Meta
                title={
                  <Link 
                    to={`/post/${post.id}`}
                    state={{ fromArchive: true }}
                    style={{ fontSize: 16 }}
                  >
                    {post.title}
                  </Link>
                }
                description={
                  <div>
                    <Space style={{ marginBottom: 4 }}>
                      {post.tags && post.tags.slice(0, 3).map(tag => (
                        <Tag key={tag} size="small">{tag}</Tag>
                      ))}
                    </Space>
                    <div>
                      <Text type="secondary">
                        <CalendarOutlined /> {post.date || '未知日期'} · 
                        <EyeOutlined /> {post.views || 0} · 
                        <LikeOutlined /> {post.likes || 0}
                      </Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    </div>
  );
};

export default Archive;